import { useState, useEffect, useRef, useContext } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import ServiceCard from '../ServiceView/component/ServiceCard';
import useServices from '../../stores/useServices';
import Skeleton from '../../components/ui/skeleton';
import Heading from '../../components/Heading';
import { SERVICE_PAGE } from '../../routes/routeConstant';
import { LanguageContext } from '../../contexts/LanguageContext';

const SERVICE_CARD_WIDTH = 300;
const SERVICE_CARD_GAP = 8;

export default function ServiceSlide() {
  const { services, hasFetched, fetchLoading, fetchServices } = useServices();
  const [slidesPerView, setSlidesPerView] = useState(1.5);
  const containerRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const { language } =
    useContext(LanguageContext) ||
    (() => {
      throw new Error('LanguageSwitcher must be used within a LanguageProvider');
    })();
  const lang = language === 'id';

  useEffect(() => {
    if (!hasFetched) fetchServices();
  }, [fetchServices, hasFetched]);

  useEffect(() => {
    const updateSlidesPerView = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const newSlidesPerView = containerWidth / (SERVICE_CARD_WIDTH + SERVICE_CARD_GAP);
        setSlidesPerView(newSlidesPerView);
      }
    };

    updateSlidesPerView();
    window.addEventListener('resize', updateSlidesPerView);
    return () => window.removeEventListener('resize', updateSlidesPerView);
  }, []);

  return (
    <section ref={containerRef} className="relative w-full">
      <div className="flex justify-between w-full items-center mb-4">
        <Heading>{t('footer.services.title')}</Heading>
        <Link to={SERVICE_PAGE} className="p-2 hover:bg-neutral-900/10 rounded-full">
          <ArrowRight />
        </Link>
      </div>
      <div className="relative">
        <Swiper
          modules={[Navigation]}
          slidesPerView={slidesPerView}
          spaceBetween={SERVICE_CARD_GAP}
          className="relative"
        >
          {fetchLoading ? (
            <>
              {Array.from({ length: 10 }).map(() => (
                <SwiperSlide key={Math.random()} style={{ width: `${SERVICE_CARD_WIDTH}px` }}>
                  <Skeleton className={`w-[${SERVICE_CARD_WIDTH}px] shadow-md h-32`} />
                </SwiperSlide>
              ))}
            </>
          ) : (
            <>
              {services.map((srv) => (
                <SwiperSlide key={srv.service_id} className="pb-4" style={{ width: `${SERVICE_CARD_WIDTH}px` }}>
                  <ServiceCard
                    id={srv.service_id}
                    desc={lang ? srv.description_srv_id : srv.description_srv_en || srv.description_srv_id}
                    name={lang ? srv.name_srv_id : srv.name_srv_en || srv.name_srv_id}
                    image={srv.image_srv || '/assets/no-data-image.svg'}
                    className="w-full"
                  />
                </SwiperSlide>
              ))}
            </>
          )}
        </Swiper>
      </div>
    </section>
  );
}
